/**
 * Шаблоны писем врачу и подстановка значений.
 *
 * Пациент подставляется В РОДИТЕЛЬНОМ ПАДЕЖЕ. Письма почти всегда строятся
 * оборотом «заказ для пациента …» или «работа по пациенту …», и именительный
 * падеж в них читается как ошибка: «Заказ для пациента Иванова М. принят» —
 * нужно «Ивановой М.».
 *
 * Именительный никуда не делся: он под своей подстановкой `{пациент_им}` —
 * для фраз вроде «Пациент Иванова М. записана на примерку».
 */
import { fullNameGenitive, surnameGenitive } from './signer.js';
/**
 * Женский род ПО ФАМИЛИИ, а не по отчеству.
 *
 * У пациента отчества обычно нет — в заказ пишут «Иванова М.» или просто
 * «Шечков», — поэтому единственная примета остаётся сама фамилия. Правило
 * работает не всегда (несклоняемые «Приходько», «Лю» рода не выдают), но
 * там и склонять нечего: они возвращаются неизменными.
 */
function feminineSurname(surname) {
    return /(ова|ева|ёва|ина|ына|ская|цкая|ая)$/i.test(surname.trim());
}
/** Похоже ли слово на отчество: по нему определяется род в полном ФИО. */
function looksLikePatronymic(word) {
    return /(ович|евич|ич|овна|евна|ична|инична)$/i.test(word.trim());
}
/**
 * Родительный падеж имени пациента.
 *
 * Пациента записывают по-разному: «Шечков», «Иванова М.», «Манзырев Т.»,
 * изредка полным ФИО. Инициалы не склоняются и остаются как есть — склоняется
 * только фамилия.
 *
 * Пустая строка возвращается пустой: заказ нередко заводят по звонку, когда
 * фамилию ещё не назвали, и подстановка не должна выдумывать её за врача.
 */
export function patientGenitive(patient) {
    const parts = patient.trim().split(/\s+/).filter(Boolean);
    const [surname, ...rest] = parts;
    if (!surname)
        return '';
    // Полное ФИО — у него род определяется по отчеству, это надёжнее фамилии.
    if (rest.length >= 2 && looksLikePatronymic(rest[rest.length - 1])) {
        return fullNameGenitive(patient);
    }
    /*
     * `surnameGenitive` определяет род по отчеству, которого здесь нет.
     * Подставляем вместо него подходящее по роду окончание — так функция
     * получает признак, ради которого отчество ей и нужно.
     */
    const declined = surnameGenitive(surname, feminineSurname(surname) ? 'овна' : 'ович');
    return [declined, ...rest].join(' ');
}
/**
 * Подстановки шаблона.
 *
 * Список ЗДЕСЬ, а не в тексте подсказки на экране: подсказка и подстановка
 * обязаны совпадать, иначе пользователь наберёт то, чего программа не знает,
 * и увидит фигурные скобки в письме врачу.
 */
export const LETTER_PLACEHOLDERS = [
    { token: '{врач}', hint: 'фамилия врача' },
    { token: '{пациент}', hint: 'пациент в родительном падеже: «для Ивановой М.»' },
    { token: '{пациент_им}', hint: 'пациент как записан: «Иванова М.»' },
    { token: '{конструкция}', hint: 'что изготавливается, вместе с доп. элементами' },
    { token: '{дата_сдачи}', hint: 'срок' },
];
/**
 * Подстановка значений в шаблон письма.
 *
 * Неизвестные фигурные скобки остаются как есть: молча стирать чужой текст
 * нельзя, а увидев `{пацент}` в предпросмотре, опечатку сразу видно.
 */
export function fillLetter(template, values) {
    const map = {
        '{врач}': values.doctor,
        '{пациент}': patientGenitive(values.patient),
        '{пациент_им}': values.patient,
        '{конструкция}': values.construction,
        '{дата_сдачи}': values.due,
    };
    const filled = template.replace(/\{[а-яё_]+\}/gi, (token) => map[token] ?? token);
    /*
     * Пустая подстановка не должна оставлять дыру.
     *
     * Заказ бывает без пациента — его вписывают позже. Тогда «на пациента
     * {пациент} (аппарат)» превращалось в «на пациента  (аппарат)» с двойным
     * пробелом, и письмо выглядело сломанным. Текст перед отправкой правится
     * вручную, но начинать правку с уборки пробелов — плохое начало.
     */
    return filled
        .replace(/[ \t]{2,}/g, ' ')
        .replace(/[ \t]+([,.;:!?)])/g, '$1')
        .replace(/\(\s+/g, '(')
        .trim();
}
/**
 * Тема письма.
 *
 * В шаблонах её нет и не будет: владелец правит там ТЕКСТ, а тема — служебная
 * строка, по которой врач находит письмо в ящике. Пациент в теме стоит в
 * именительном падеже: тема читается как заголовок, а не как фраза.
 */
export function letterSubject(kind, patient) {
    const who = patient.trim();
    const what = kind === 'ACCEPTED' ? 'Заказ принят' : 'Заказ готов';
    return who ? `${what} — ${who}` : what;
}
/**
 * Собрать письмо из шаблона и данных заказа.
 *
 * Живёт в общем ядре, а не на сервере: то же самое собирается в окне
 * подтверждения перед отправкой. Считай их порознь — врач получил бы не то,
 * что владелец видел на экране, и заметил бы это только он.
 */
export function composeLetter(kind, templates, values) {
    const template = kind === 'ACCEPTED' ? templates.letter1 : templates.letter2;
    return {
        subject: letterSubject(kind, values.patient),
        body: fillLetter(template, values),
    };
}
/**
 * Какое письмо уместно по заказу с таким статусом.
 *
 * Живёт здесь, а не в экране: по этому правилу и подписывается кнопка в
 * карточке заказа, и красится отметка в списке. Разойдись они — список
 * показывал бы одно письмо, а карточка предлагала отправить другое.
 */
export function letterDue(status) {
    return status === 'Готово' ? 'READY' : 'ACCEPTED';
}
// ---------------------------------------------------------------------------
// Письмо клинике со счётом и актом
// ---------------------------------------------------------------------------
/** Текст сопроводительного письма по умолчанию, если свой не задан. */
export const DEFAULT_INVOICE_LETTER = [
    'Здравствуйте!',
    '',
    'Отправляем Вам счёт и акт выполненных работ.',
    '',
    'С уважением,',
    'Лаборатория OrthoTech',
].join('\n');
/**
 * Сопроводительное письмо к счёту.
 *
 * Текст берётся из шаблона лаборатории, а если он пуст — из значения по
 * умолчанию. Номер и дата в теле не повторяются: они стоят в теме и во
 * вложениях, а третье место, где их можно разойтись, ни к чему.
 *
 * Перед отправкой текст правится в окне — для случаев, когда бухгалтерии
 * нужно пояснить что-то отдельно.
 */
export function composeInvoiceLetter(values) {
    return {
        subject: `Счёт № ${values.number} от ${values.date}`,
        body: values.template?.trim() ? values.template.trim() : DEFAULT_INVOICE_LETTER,
    };
}
//# sourceMappingURL=letter.js.map